import { useMemo, useState, type CSSProperties } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  Legend,
} from 'recharts';
import { allocationsApi, stockApi } from '@/features/stock/api';
import { brandsApi } from '@/features/brands/api';
import { eventsApi } from '@/features/events/api';
import { queryKeys } from '@/lib/api/queryKeys';
import { useSelectedBrandId } from '@/lib/branding/brandContextStore';
import { ContactsPage, type ContactsScope } from '@/features/contacts/ContactsPage';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { Select } from '@/components/ui/Input';
import { MultiSelect } from '@/components/ui/MultiSelect';
import { TagFilter } from '@/components/ui/TagFilter';
import type { PickerTag } from '@/components/ui/TagPicker';
import { useTagAssignments, useTags } from '@/lib/api/tags';
import { useAuth } from '@/lib/auth/useAuth';
import { DatePicker } from '@/components/ui/DatePicker';
import { Button } from '@/components/ui/Button';
import { THEME } from '@/lib/theme';
import { useT } from '@/lib/i18n/useT';
import { useFavoritable } from '@/features/favorites/FavoritableContext';
// PRD §10 — Atledi Recap hero card.
import { AtlediRecapCard } from './AtlediRecapCard';
// PRD §10 — PDF export via browser print, gated by plan flag.
import { useFeatureAccess } from '@onxite/plan-sdk';
import { Download as DownloadIcon } from 'lucide-react';
import { HelpHint } from '@/features/help/HelpHint';
// Caso 09 v1 — workspace-wide demographic snapshot. Lives at the page
// bottom so it doesn't push the stock cards (the primary content) below
// the fold.
import { DemographicsPanel } from '@/features/demographics/DemographicsPanel';
import { SurveyDemographics } from '@/features/demographics/SurveyDemographics';
import type { XkEvent, XkEventStockStation } from '@/lib/api/types';
import dayjs from 'dayjs';
import { factsApi, type EventFacts } from './factsApi';
import {
  aggregateFacts,
  bigOf,
  fmtInt,
  fmtPct,
  ledgerOf,
  mergeLedgers,
  goalOf,
  phaseOf,
  throughputByClock,
  throughputOf,
  type ThroughputSeries,
} from './engine';
import { HeroTiles } from './charts/HeroTiles';
import { useTypewriter } from './motion/useTypewriter';
import { useScrollRamp } from './motion/useScrollRamp';
import { LedgerBars } from './charts/LedgerBars';
import { ThroughputTimeline } from './charts/ThroughputTimeline';
import { EventStrip, type StripEvent, type TagPreset } from './charts/EventStrip';
import { KpiCard } from './charts/MiniViz';
import { CompareMatrix, type CompareGroupInput } from './charts/CompareMatrix';
import { MAX_GROUPS } from './stats';

const COLORS = [
  THEME.accent,
  THEME.accentHover,
  THEME.success,
  THEME.warn,
  THEME.error,
  THEME.info,
];

/**
 * Tooltip de las gráficas.
 *
 * Va con los tokens del lienzo CLARO, que es donde vive el admin desde el
 * rediseño. Estuvo en `THEME.navy` con el texto sin declarar: recharts lo
 * pinta entonces en su gris oscuro por defecto, o sea tinta oscura sobre
 * fondo oscuro — el rótulo no se leía. `color` se declara explícitamente
 * para que no dependa de lo que herede del DOM de alrededor.
 */
const TOOLTIP_STYLE: CSSProperties = {
  backgroundColor: 'var(--color-bg-surface)',
  border: '1px solid var(--color-border-default)',
  borderRadius: 'var(--radius-lg)',
  fontSize: 12,
  color: 'var(--color-fg-primary)',
};

/** El velo de la barra bajo el cursor. Blanco sobre blanco no se veía. */
const TOOLTIP_CURSOR = { fill: 'var(--color-bg-elevated)' };

/**
 * El texto de cada serie dentro del tooltip.
 *
 * Recharts lo pinta con el color de la serie, así que una barra clara —la de
 * stock disponible— dejaba su línea en gris casi invisible sobre el fondo
 * blanco. El color sigue estando en la barra y en la leyenda; el texto va en
 * tinta para que se lea siempre.
 */
const TOOLTIP_ITEM_STYLE: CSSProperties = { color: 'var(--color-fg-primary)' };

/**
 * La leyenda, con el mismo problema y la misma cura: recharts pinta cada
 * etiqueta con el color de su serie, y sobre blanco los verdes claros de la
 * paleta se pierden. El cuadrito de color sigue diciendo cuál es cuál.
 */
const legendInk = (value: string) => (
  <span style={{ color: 'var(--color-fg-primary)' }}>{value}</span>
);

interface FilterState {
  /**
   * A project is an event group (`xk-event-group`) — the same thing the
   * sidebar calls Projects. Filtering by it is how an agency asks "how did
   * THIS campaign do", which is the question the classification filter never
   * answered: classification describes an item, not a piece of work.
   */
  projectId: number | '';
  /**
   * Uno o varios eventos; vacío es «todos los del alcance». Un array y no
   * un id: la pregunta de Insights es «qué es verdad en ESTOS eventos», y
   * eso no cabe en un select de uno.
   */
  eventIds: number[];
  /**
   * Etiquetas de evento y de proyecto, con Y lógico: un evento pasa si lleva
   * cada etiqueta marcada, él o su proyecto. Ids de `xk_tag`, que son únicos
   * entre tipos.
   */
  tagIds: number[];
  /** ISO date string `YYYY-MM-DD` or empty. */
  from: string;
  to: string;
}

const EMPTY_FILTERS: FilterState = {
  projectId: '',
  eventIds: [],
  tagIds: [],
  from: '',
  to: '',
};



/**
 * Insights overview.
 *
 * The backend exposes per-scope reports under `/xk-event-stock-station/report/*`,
 * but the agency overview needs a cross-cutting view (across brands, projects,
 * events and time). Rather than ask for a new endpoint we aggregate
 * client-side on top of `allocationsApi.list()` + `stockApi.list()` + the
 * events/brands tree. Volumes are modest (an agency rarely sees more than a
 * few thousand allocations) so this is fine for now; if it ever gets slow we
 * can migrate the heavy lifts behind a dedicated `/xk-insights/overview`.
 */
// PRD §10 — Insights tab navigation. Filter bar + PDF button stay above
// the tabs so they're scoped universally; each tab renders its own
// section beneath the same filtered dataset.
type InsightsTab = 'recap' | 'throughput' | 'distribution' | 'demographics' | 'leads' | 'compare';
const INSIGHTS_TABS: InsightsTab[] = ['recap', 'throughput', 'distribution', 'demographics', 'leads', 'compare'];
/** Cómo se agrupa el alcance para comparar. */
type CompareBy = 'project' | 'tag' | 'event' | 'custom';

export function InsightsPage() {
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [activeTab, setActiveTab] = useState<InsightsTab>('recap');
  // Brand is implicit — taken from the workspace switcher. The previous
  // explicit brand filter has been removed.
  const selectedBrandId = useSelectedBrandId();
  const t = useT();
  useFavoritable(t('sidebar.insights'));

  // ── Data ──────────────────────────────────────────────────────────────
  const brandsQ = useQuery({
    queryKey: queryKeys.brands.all,
    queryFn: () => brandsApi.list(),
  });
  const stocksQ = useQuery({
    queryKey: queryKeys.stock.all,
    queryFn: () => stockApi.list(),
  });
  const allocsQ = useQuery({
    // 0 here is the "all stations" sentinel used elsewhere on this page.
    queryKey: queryKeys.stockAllocations.byStation(0),
    queryFn: () => allocationsApi.list(),
  });
  const eventsQ = useQuery({
    queryKey: queryKeys.events.all,
    queryFn: () => eventsApi.listEvents(),
  });
  const groupsQ = useQuery({
    queryKey: queryKeys.eventGroups.all,
    queryFn: () => eventsApi.listGroups(),
  });

  const brands = brandsQ.data?.items ?? [];
  const stocks = stocksQ.data?.items ?? [];
  const allocations = allocsQ.data?.items ?? [];
  const events = eventsQ.data?.items ?? [];
  const groups = groupsQ.data?.items ?? [];

  // ── Lookups ───────────────────────────────────────────────────────────
  const stockById = useMemo(() => new Map(stocks.map((s) => [s.id, s])), [stocks]);
  const eventById = useMemo(() => new Map(events.map((e) => [e.id, e])), [events]);
  const groupById = useMemo(() => new Map(groups.map((g) => [g.id, g])), [groups]);
  const brandById = useMemo(() => new Map(brands.map((b) => [b.id, b])), [brands]);

  /** brand id for a given event id, by walking event → group → companyId */
  const brandOfEvent = useMemo(() => {
    const map = new Map<number, number | undefined>();
    for (const e of events) {
      const g = groupById.get(e.eventGroupId);
      map.set(e.id, g?.companyId ?? undefined);
    }
    return map;
  }, [events, groupById]);

  // ── Project list narrowed by the active brand ─────────────────────────
  const projectsForFilter = useMemo(() => {
    if (!selectedBrandId) return groups;
    return groups.filter((g) => g.companyId === selectedBrandId);
  }, [groups, selectedBrandId]);

  // ── Event list narrowed by the active brand and the chosen project ────
  const eventsForFilter = useMemo(() => {
    let list = events;
    if (selectedBrandId) list = list.filter((e) => brandOfEvent.get(e.id) === selectedBrandId);
    if (filters.projectId) list = list.filter((e) => e.eventGroupId === filters.projectId);
    return list;
  }, [events, selectedBrandId, brandOfEvent, filters.projectId]);

  // ── Etiquetas de eventos y proyectos ──────────────────────────────────
  // El vocabulario es de la marca; sin marca elegida, el de la empresa propia.
  const { companyId: miEmpresa } = useAuth();
  const tagCompany = selectedBrandId ?? miEmpresa ?? null;
  const eventTagVocab = useTags(tagCompany, 'event').data ?? [];
  const projectTagVocab = useTags(tagCompany, 'project').data ?? [];
  const brandEventIds = useMemo(() => eventsForFilter.map((e) => e.id), [eventsForFilter]);
  const brandGroupIds = useMemo(() => projectsForFilter.map((g) => g.id), [projectsForFilter]);
  const eventTagsQ = useTagAssignments(tagCompany, 'event', brandEventIds);
  const projectTagsQ = useTagAssignments(tagCompany, 'project', brandGroupIds);
  /** Las etiquetas de cada evento: las suyas más las de su proyecto. */
  const tagsOfEvent = useMemo(() => {
    const out = new Map<number, Set<number>>();
    for (const e of eventsForFilter) {
      const set = new Set<number>();
      for (const tg of eventTagsQ.data?.[e.id] ?? []) set.add(tg.id);
      for (const tg of projectTagsQ.data?.[e.eventGroupId] ?? []) set.add(tg.id);
      out.set(e.id, set);
    }
    return out;
  }, [eventsForFilter, eventTagsQ.data, projectTagsQ.data]);
  /** Un solo vocabulario para el filtro; las de proyecto se distinguen por el sufijo. */
  const tagVocab = useMemo<PickerTag[]>(() => {
    const used = new Set<number>();
    for (const set of tagsOfEvent.values()) for (const id of set) used.add(id);
    return [
      ...eventTagVocab.filter((tg) => used.has(tg.id)).map((tg) => ({ id: tg.id, name: tg.name, color: tg.color })),
      ...projectTagVocab.filter((tg) => used.has(tg.id)).map((tg) => ({ id: tg.id, name: t('insights.filter.projectTag', { name: tg.name }), color: tg.color })),
    ];
  }, [eventTagVocab, projectTagVocab, tagsOfEvent, t]);

  /**
   * EL alcance. Un solo conjunto de eventos —marca, proyecto, ventana de
   * fechas, etiquetas y la selección múltiple— del que beben TODAS las pestañas.
   *
   * Antes cada pestaña acotaba por su cuenta: las asignaciones miraban el
   * evento y las fechas, la demografía sólo la marca y el proyecto, y la
   * tabla de leads iba por otro camino. El resultado era que cambiar de
   * evento movía unas gráficas y otras no. Ahora el alcance se calcula una
   * vez y lo demás sólo pregunta «¿está este evento en el conjunto?».
   */
  const eventsInWindow = useMemo(() => {
    const fromMs = filters.from ? new Date(filters.from).getTime() : null;
    const toMs = filters.to ? new Date(filters.to + 'T23:59:59').getTime() : null;
    return eventsForFilter.filter((e) => {
      if (fromMs != null || toMs != null) {
        const ms = e.startDate ? new Date(e.startDate).getTime() : null;
        if (ms == null) return false; // sin fecha no pasa un filtro de fechas
        if (fromMs != null && ms < fromMs) return false;
        if (toMs != null && ms > toMs) return false;
      }
      if (filters.tagIds.length) {
        const mine = tagsOfEvent.get(e.id);
        if (!mine || !filters.tagIds.every((id) => mine.has(id))) return false;
      }
      return true;
    });
  }, [eventsForFilter, filters.from, filters.to, filters.tagIds, tagsOfEvent]);

  const scopedEvents = useMemo(() => {
    if (filters.eventIds.length === 0) return eventsInWindow;
    const picked = new Set(filters.eventIds);
    return eventsInWindow.filter((e) => picked.has(e.id));
  }, [eventsInWindow, filters.eventIds]);

  const scopedEventIds = useMemo(() => scopedEvents.map((e) => e.id).sort((a, b) => a - b), [scopedEvents]);
  const scopedIdSet = useMemo(() => new Set(scopedEventIds), [scopedEventIds]);

  /**
   * Los hechos de todos los eventos de la ventana, no sólo de los elegidos:
   * la línea de eventos dibuja también los que no están en la selección,
   * para que se vea qué se está dejando fuera. La selección se aplica aquí,
   * en memoria, así que marcar y desmarcar no vuelve a pedir nada.
   */
  const windowIds = useMemo(() => eventsInWindow.map((e) => e.id).sort((a, b) => a - b), [eventsInWindow]);
  const factsQ = useQuery({
    queryKey: queryKeys.insights.facts(windowIds),
    queryFn: () => factsApi.forEvents(windowIds),
    enabled: windowIds.length > 0,
    staleTime: 30_000,
    // Al cambiar etiquetas o fechas la ventana cambia y se piden hechos
    // nuevos; mientras llegan, la línea sigue con los anteriores en vez de
    // caer a cero un segundo.
    placeholderData: keepPreviousData,
  });
  const selectedFacts = useMemo<EventFacts[]>(
    () => (factsQ.data?.events ?? []).filter((f) => scopedIdSet.has(f.event.id)),
    [factsQ.data, scopedIdSet],
  );
  const agg = useMemo(() => aggregateFacts(selectedFacts), [selectedFacts]);
  const byItem = useMemo(() => mergeLedgers(selectedFacts), [selectedFacts]);
  const itemTotals = useMemo(() => ledgerOf(byItem), [byItem]);
  // Primero la selección entera proyectada sobre la hora del día; después,
  // un día por evento. Con muchos eventos el panel ofrece un navegador en
  // vez de una píldora por serie.
  const throughputSeries = useMemo<ThroughputSeries[]>(() => {
    const perEvent = selectedFacts.flatMap((f) =>
      throughputOf(f.throughput).map((s) => ({ ...s, name: selectedFacts.length > 1 ? f.event.name : undefined })),
    );
    if (selectedFacts.length <= 1) return perEvent;
    const all = throughputByClock(selectedFacts);
    return all ? [{ ...all, name: t('insights.throughput.allSelection'), dayLabel: t('insights.throughput.byClock') }, ...perEvent] : perEvent;
  }, [selectedFacts, t]);

  /**
   * Los mismos filtros, traducidos a lo que entiende /co-contacts.
   *
   * La pestaña de Leads embebe la pantalla de Contactos entera. Recibe el
   * alcance como lista de eventos —la misma que ven las demás pestañas— y
   * no como un `eventId` suelto: así la tabla y el CSV cuentan la misma
   * gente que la demografía y la distribución. Sin nada que acote más allá
   * de marca y proyecto se dejan esos dos, que el servidor resuelve solo y
   * siguen valiendo si mañana se añade un evento al proyecto.
   */
  const narrowed = filters.eventIds.length > 0 || filters.from !== '' || filters.to !== '' || filters.tagIds.length > 0;
  const leadsScope = useMemo<ContactsScope>(
    () => ({
      brandId: selectedBrandId ?? undefined,
      eventGroupId: filters.projectId || undefined,
      // Sin eventos en el alcance la tabla debe salir vacía, no entera.
      eventIds: narrowed ? (scopedEventIds.length ? scopedEventIds : [-1]) : undefined,
      from: filters.from || undefined,
      to: filters.to ? `${filters.to}T23:59:59` : undefined,
    }),
    [selectedBrandId, filters.projectId, narrowed, scopedEventIds, filters.from, filters.to],
  );

  // ── Apply the scope to allocations ────────────────────────────────────
  const filteredAllocs = useMemo(
    () => allocations.filter((a) => scopedIdSet.has(a.eventId)),
    [allocations, scopedIdSet],
  );

  const loading =
    brandsQ.isLoading || stocksQ.isLoading || allocsQ.isLoading || eventsQ.isLoading || groupsQ.isLoading;

  // ── La línea de eventos ───────────────────────────────────────────────
  const stripEvents = useMemo<StripEvent[]>(() => {
    const facts = new Map((factsQ.data?.events ?? []).map((f) => [f.event.id, f]));
    return [...eventsInWindow]
      .sort((a, b) => (a.startDate ?? '').localeCompare(b.startDate ?? ''))
      .map<StripEvent>((e) => {
        const f = facts.get(e.id);
        const leadGoal = f ? goalOf(f, 'leads') : null;
        const prizeGoal = f ? goalOf(f, 'prizesDelivered') : null;
        return {
          id: e.id,
          name: e.name,
          date: e.startDate ?? null,
          contacts: f?.funnel.contacts ?? 0,
          responses: f?.funnel.responses ?? 0,
          phase: f ? phaseOf(f) : 'edit',
          projectId: e.eventGroupId ?? null,
          projectName: groupById.get(e.eventGroupId)?.name ?? '',
          beatPlan: !!f && ((leadGoal != null && f.funnel.leads >= leadGoal) || (prizeGoal != null && f.funnel.prizesDelivered >= prizeGoal)),
          selected: scopedIdSet.has(e.id),
        };
      });
  }, [eventsInWindow, factsQ.data, scopedIdSet, groupById]);

  /** Presets por etiqueta: cada etiqueta con al menos un evento en la ventana. */
  const tagPresets = useMemo<TagPreset[]>(() => {
    const all = [...eventTagVocab, ...projectTagVocab];
    return all
      .map((tg) => ({
        id: tg.id,
        label: tg.name,
        color: tg.color,
        ids: eventsInWindow.filter((e) => tagsOfEvent.get(e.id)?.has(tg.id)).map((e) => e.id),
      }))
      .filter((p) => p.ids.length > 0)
      .sort((a, b) => b.ids.length - a.ids.length);
  }, [eventTagVocab, projectTagVocab, eventsInWindow, tagsOfEvent]);

  // ── Comparar: cualquier agrupación del alcance ─────────────────────────
  const [compareBy, setCompareBy] = useState<CompareBy>('project');
  /** Conjuntos a medida: hasta tres listas de eventos del alcance. */
  const [customSets, setCustomSets] = useState<number[][]>([[], []]);
  /** Qué grupos entran por modo; vacío = los primeros del modo. */
  const [pickedGroups, setPickedGroups] = useState<Record<CompareBy, string[]>>({ project: [], tag: [], event: [], custom: [] });
  const candidateGroups = useMemo<CompareGroupInput[]>(() => {
    const byId = new Map(selectedFacts.map((f) => [f.event.id, f]));
    if (compareBy === 'project') {
      const m = new Map<number, EventFacts[]>();
      for (const f of selectedFacts) {
        const gid = f.event.eventGroupId ?? 0;
        m.set(gid, [...(m.get(gid) ?? []), f]);
      }
      return [...m.entries()].map(([gid, facts]) => ({ key: `p${gid}`, label: groupById.get(gid)?.name ?? `#${gid}`, facts }));
    }
    if (compareBy === 'tag') {
      return tagPresets
        .map((tg) => ({ key: `t${tg.id}`, label: tg.label, facts: tg.ids.map((id) => byId.get(id)).filter((f): f is EventFacts => !!f) }))
        .filter((g) => g.facts.length > 0);
    }
    if (compareBy === 'event') {
      return selectedFacts
        .filter((f) => f.funnel.interactions > 0 || f.funnel.prizesDelivered > 0)
        .map((f) => ({ key: `e${f.event.id}`, label: f.event.name, facts: [f] }));
    }
    return customSets
      .map((ids, i) => ({ key: `c${i}`, label: t('insights.compare.customSet', { n: String.fromCharCode(65 + i) }), facts: ids.map((id) => byId.get(id)).filter((f): f is EventFacts => !!f) }))
      .filter((g) => g.facts.length > 0);
  }, [compareBy, selectedFacts, groupById, tagPresets, customSets, t]);
  // Un proyecto puede tener cincuenta eventos y una marca cien etiquetas: los
  // grupos que entran se eligen en un selector; sin elegir, los primeros.
  const compareGroups = useMemo<CompareGroupInput[]>(() => {
    if (compareBy === 'custom') return candidateGroups;
    const picked = pickedGroups[compareBy];
    if (picked.length === 0) return candidateGroups.slice(0, MAX_GROUPS);
    return candidateGroups.filter((g) => picked.includes(g.key)).slice(0, MAX_GROUPS);
  }, [compareBy, candidateGroups, pickedGroups]);

  /** `null` = todo el alcance; un array es una selección real. */
  function pickEvents(ids: number[] | null) {
    setFilters((f) => ({ ...f, eventIds: ids === null || ids.length === eventsInWindow.length ? [] : ids }));
  }

  const sentence = (() => {
    if (scopedEvents.length === 0) return t('insights.scope.none');
    const first = agg.first?.event.startDate;
    const last = agg.last?.event.endDate ?? agg.last?.event.startDate;
    const projects = new Set(scopedEvents.map((e) => e.eventGroupId)).size;
    return t('insights.scope.sentence', {
      n: scopedEvents.length,
      total: eventsInWindow.length,
      from: first ? dayjs(first).format('MMM D') : '—',
      to: last ? dayjs(last).format('MMM D') : '—',
      projects,
    });
  })();

  // ── KPIs ──────────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    let distributed = 0;
    let total = 0;
    let value = 0;
    let restockAlerts = 0;
    const brandIds = new Set<number>();
    const eventIds = new Set<number>();

    for (const a of filteredAllocs) {
      const used = Math.max(0, (a.quantity ?? 0) - (a.available ?? 0));
      distributed += used;
      total += a.quantity ?? 0;
      const s = stockById.get(a.stockId);
      if (s?.unitCost) value += used * Number(s.unitCost);
      if (s?.restockThreshold != null && s.restockThreshold > 0 && a.available <= s.restockThreshold) {
        restockAlerts++;
      }
      const bId = brandOfEvent.get(a.eventId);
      if (bId) brandIds.add(bId);
      eventIds.add(a.eventId);
    }

    return {
      distributed,
      remaining: Math.max(0, total - distributed),
      total,
      rate: total > 0 ? Math.round((distributed / total) * 100) : 0,
      value,
      restockAlerts,
      brandCount: brandIds.size,
      eventCount: eventIds.size,
    };
  }, [filteredAllocs, stockById, brandOfEvent]);

  const activeEventsCount = useMemo(() => activeWindow(eventsForFilter), [eventsForFilter]);

  // ── Chart datasets ────────────────────────────────────────────────────
  const byBrand = useMemo(
    () => bucket(filteredAllocs, (a) => brandOfEvent.get(a.eventId), (id) => brandById.get(id ?? -1)?.name),
    [filteredAllocs, brandOfEvent, brandById],
  );

  const byEvent = useMemo(
    () => bucket(filteredAllocs, (a) => a.eventId, (id) => eventById.get(id ?? -1)?.name).slice(0, 10),
    [filteredAllocs, eventById],
  );

  const topStocks = useMemo(
    () => bucket(filteredAllocs, (a) => a.stockId, (id) => stockById.get(id ?? -1)?.name).slice(0, 10),
    [filteredAllocs, stockById],
  );

  const byClassification = useMemo(() => {
    const unclassifiedLabel = t('insights.unclassifiedLabel');
    const acc: Record<string, number> = {};
    for (const a of filteredAllocs) {
      const used = Math.max(0, (a.quantity ?? 0) - (a.available ?? 0));
      const s = stockById.get(a.stockId);
      const k = s?.classification ?? unclassifiedLabel;
      acc[k] = (acc[k] ?? 0) + used;
    }
    return Object.entries(acc)
      .filter(([, v]) => v > 0)
      .map(([name, value]) => ({ name, value }));
  }, [filteredAllocs, stockById, t]);

  const byRarity = useMemo(() => {
    const labels: Record<number, string> = {
      1: t('insights.rarity.common'),
      2: t('insights.rarity.uncommon'),
      3: t('insights.rarity.rare'),
      4: t('insights.rarity.epic'),
      5: t('insights.rarity.legendary'),
    };
    const acc: Record<number, number> = {};
    for (const a of filteredAllocs) {
      const used = Math.max(0, (a.quantity ?? 0) - (a.available ?? 0));
      const s = stockById.get(a.stockId);
      const r = Math.max(1, Math.min(5, s?.rating ?? 3));
      acc[r] = (acc[r] ?? 0) + used;
    }
    return Object.entries(acc)
      .filter(([, v]) => v > 0)
      .map(([k, value]) => ({ name: labels[Number(k)] ?? t('insights.rarity.tier', { n: k }), value }));
  }, [filteredAllocs, stockById, t]);

  const stockUsage = useMemo(() => {
    const acc: Record<number, { used: number; available: number }> = {};
    for (const a of filteredAllocs) {
      const cur = acc[a.stockId] ?? { used: 0, available: 0 };
      const used = Math.max(0, (a.quantity ?? 0) - (a.available ?? 0));
      acc[a.stockId] = {
        used: cur.used + used,
        available: cur.available + (a.available ?? 0),
      };
    }
    return Object.entries(acc)
      .map(([id, v]) => ({
        name: stockById.get(Number(id))?.name ?? `#${id}`,
        used: v.used,
        available: v.available,
      }))
      .sort((a, b) => b.used + b.available - (a.used + a.available))
      .slice(0, 10);
  }, [filteredAllocs, stockById]);

  // ── Render ────────────────────────────────────────────────────────────
  const hasFilters =
    filters.projectId !== '' ||
    filters.eventIds.length > 0 ||
    filters.tagIds.length > 0 ||
    filters.from !== '' ||
    filters.to !== '';

  return (
    <div className="page space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold">
            {t('insights.title')}
            <HelpHint section="insights" />
          </h1>
          <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
            {t('insights.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasFilters && (
            <Button variant="ghost" onClick={() => setFilters(EMPTY_FILTERS)}>
              {t('insights.resetFilters')}
            </Button>
          )}
          <PdfExportButton />
        </div>
      </header>

      {/* PRD §10 — moved to the Recap tab below. */}

      {/* ── Filter bar — brand filter removed; scope is the active brand
          from the workspace switcher. */}
      <Card data-tour="insights-filters">
        <CardContent className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-5">
          <FilterField label={t('insights.filter.project')}>
            <Select
              value={filters.projectId === '' ? '' : String(filters.projectId)}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  projectId: e.target.value ? Number(e.target.value) : '',
                  // The picked events may not belong to this project; leaving
                  // them set would show an empty page without saying why.
                  eventIds: [],
                }))
              }
            >
              <option value="">
                {t('insights.filter.allProjects', { count: projectsForFilter.length })}
              </option>
              {projectsForFilter.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </Select>
          </FilterField>

          <FilterField label={t('insights.filter.event')}>
            <MultiSelect
              options={eventsForFilter.map((ev) => ({
                value: ev.id,
                label: ev.name,
                hint: ev.startDate ? dayjs(ev.startDate).format('MMM D, YYYY') : undefined,
              }))}
              value={filters.eventIds}
              onChange={(next) => setFilters((f) => ({ ...f, eventIds: next }))}
              placeholder={t('insights.filter.allEvents', { count: eventsForFilter.length })}
              searchPlaceholder={t('insights.filter.searchEvents')}
              summary={(n) => t('insights.filter.eventsPicked', { n })}
              selectAllLabel={t('insights.filter.selectShown')}
              clearLabel={t('insights.filter.clear')}
              emptyLabel={t('insights.filter.noMatch')}
            />
          </FilterField>

          <FilterField label={t('insights.filter.from')}>
            <DatePicker
              mode="date"
              value={filters.from}
              max={filters.to || undefined}
              onChange={(v) => setFilters((f) => ({ ...f, from: v }))}
            />
          </FilterField>

          <FilterField label={t('insights.filter.to')}>
            <DatePicker
              mode="date"
              value={filters.to}
              min={filters.from || undefined}
              onChange={(v) => setFilters((f) => ({ ...f, to: v }))}
            />
          </FilterField>

          {/* Etiquetas de evento y de proyecto: el mismo control que en las
              listas, con Y lógico. Sin vocabulario no se pinta. */}
          <FilterField label={t('insights.filter.tags')}>
            {tagVocab.length === 0 ? (
              <span className="flex h-10 items-center text-xs text-[var(--color-fg-subtle)]">{t('insights.filter.noTags')}</span>
            ) : (
              <TagFilter
                vocabulario={tagVocab}
                valor={filters.tagIds}
                onChange={(ids) => setFilters((f) => ({ ...f, tagIds: ids, eventIds: [] }))}
                resultado={eventsInWindow.length}
              />
            )}
          </FilterField>
        </CardContent>
      </Card>

      {/* La línea de eventos: los del alcance en orden de fecha, con la
          altura por contactos. Clic para meter o sacar uno de la selección;
          es el mismo conjunto que el selector de arriba. */}
      {eventsInWindow.length > 0 && (
        <Card>
          <CardContent className="p-5">
            <EventStrip events={stripEvents} tagPresets={tagPresets} onChange={pickEvents} sentence={sentence} />
          </CardContent>
        </Card>
      )}

      {/* PRD §10 — Tab navigation. Filter bar above stays universal; each
          tab renders its own section against the same filtered data. */}
      <div data-tour="insights-tabs">
        <InsightsTabBar activeTab={activeTab} onChange={setActiveTab} />
      </div>

      {/* CH-15 · «Sin datos para estos filtros» sólo vale cuando los filtros
          excluyen todo. Si hay eventos en el alcance y ninguno registró una
          sola interacción, eso es otro estado: nada se capturó, y hay que
          decirlo así, en todas las pestañas. */}
      {!factsQ.isLoading &&
        selectedFacts.length > 0 &&
        selectedFacts.every((f) => f.funnel.interactions === 0) && (
          <div
            role="status"
            data-testid="insights-not-captured"
            className="rounded-lg border border-[var(--color-warn)] bg-[rgb(234_179_8/0.08)] px-4 py-3 text-sm"
          >
            <strong className="text-[var(--color-warn)]">{t('insights.notCaptured.title')}</strong>{' '}
            {t('insights.notCaptured.body', { n: selectedFacts.length })}
          </div>
        )}

      {activeTab === 'recap' && (
        <>
          <HeroTiles
            title={t('insights.recap.heroTitle')}
            chip={t('insights.recap.scopeChip', { n: scopedEvents.length })}
            tiles={[
              {
                label: t('insights.recap.contacts'),
                value: fmtInt(agg.funnel.contacts),
                number: agg.funnel.contacts,
                format: fmtInt,
                hint: t('insights.recap.contactsHint', { n: scopedEvents.length }),
                tone: 'accent',
              },
              {
                label: t('insights.recap.leads'),
                value: fmtInt(agg.funnel.leads),
                number: agg.funnel.leads,
                format: fmtInt,
                hint:
                  agg.leadRate.state === 'MEASURED'
                    ? t('insights.recap.leadsHint', { p: fmtPct(agg.leadRate.value), moe: agg.leadRate.moe ?? 0 })
                    : t('insights.recap.noBase'),
              },
              {
                label: t('insights.recap.delivered'),
                value: fmtInt(agg.ledger.delivered),
                number: agg.ledger.delivered,
                format: fmtInt,
                hint: t('insights.recap.deliveredHint', {
                  allocated: fmtInt(agg.ledger.allocated),
                  won: fmtInt(agg.ledger.won),
                  p: bigOf(agg.ledger.redemption, '—'),
                }),
              },
              {
                label: t('insights.recap.unaccounted'),
                value: fmtInt(agg.ledger.unaccounted),
                number: agg.ledger.unaccounted,
                format: fmtInt,
                hint: t('insights.recap.unaccountedHint'),
                tone: agg.ledger.unaccounted > 0 ? 'warn' : 'default',
              },
            ]}
          />
          <AtlediRecapCard
            liveKpis={{
              moments: kpis.distributed,
              throughput: kpis.rate,
              inventory: kpis.remaining,
            }}
          />
          <div className="grid gap-3 md:grid-cols-2">
            <FeedCard
              tag={t('insights.feed.plan')}
              text={t('insights.feed.planText', {
                allocated: fmtInt(agg.ledger.allocated),
                won: fmtInt(agg.ledger.won),
                delivered: fmtInt(agg.ledger.delivered),
                pending: fmtInt(agg.ledger.pending),
              })}
            />
            <FeedCard
              tag={t('insights.feed.distribution')}
              text={t('insights.feed.distributionText', {
                delivered: fmtInt(agg.ledger.delivered),
                lapsed: fmtInt(agg.ledger.expired + agg.ledger.voided),
                inPlay: fmtInt(agg.ledger.inPlay),
              })}
            />
            <FeedCard
              tag={t('insights.feed.instrument')}
              text={t('insights.feed.instrumentText', {
                surveyed: selectedFacts.filter((f) => f.stations.withSurvey > 0).length,
                n: selectedFacts.length,
                responses: fmtInt(agg.funnel.responses),
              })}
            />
            <FeedCard
              tag={t('insights.feed.consent')}
              text={
                agg.marketingRate.state === 'MEASURED'
                  ? t('insights.feed.consentText', { p: fmtPct(agg.marketingRate.value), n: fmtInt(agg.funnel.marketingOptIns) })
                  : t('insights.feed.consentPending')
              }
            />
          </div>
        </>
      )}

      {activeTab === 'throughput' && (
      <>
      {/* ── KPI strip ────────────────────────────────────────────────── */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi
          label={t('insights.kpi.distributed')}
          value={kpis.distributed.toLocaleString()}
          number={kpis.distributed}
          format={(n) => n.toLocaleString()}
          hint={t('insights.kpi.distributedHint', { rate: kpis.rate })}
          loading={loading}
        />
        <Kpi
          label={t('insights.kpi.remaining')}
          value={kpis.remaining.toLocaleString()}
          number={kpis.remaining}
          format={(n) => n.toLocaleString()}
          hint={t('insights.kpi.remainingHint', { total: kpis.total.toLocaleString() })}
          loading={loading}
        />
        <Kpi
          label={t('insights.kpi.value')}
          value={formatMoney(kpis.value)}
          number={kpis.value}
          format={formatMoney}
          hint={t('insights.kpi.valueHint')}
          loading={loading}
        />
        <Kpi
          label={t('insights.kpi.activeEvents')}
          value={activeEventsCount.toLocaleString()}
          number={activeEventsCount}
          format={(n) => n.toLocaleString()}
          hint={t(
            kpis.brandCount === 1 && kpis.eventCount === 1
              ? 'insights.kpi.activeEventsHintOne'
              : 'insights.kpi.activeEventsHintMany',
            { brands: kpis.brandCount, events: kpis.eventCount },
          )}
          loading={loading}
        />
      </div>

      {kpis.restockAlerts > 0 && (
        <div className="rounded-lg border border-[var(--color-warn)] bg-[rgb(234_179_8/0.08)] px-4 py-3 text-sm">
          <strong className="text-[var(--color-warn)]">
            {t(
              kpis.restockAlerts === 1
                ? 'insights.restockAlertOne'
                : 'insights.restockAlertMany',
              { count: kpis.restockAlerts },
            )}
          </strong>{' '}
          {t('insights.restockRefill')}
        </div>
      )}

      {factsQ.isLoading ? (
        <Skeleton className="h-56 w-full" />
      ) : (
        <ThroughputTimeline
          series={throughputSeries}
          title={t('insights.throughput.title')}
          claimRate={agg.funnel.interactions ? agg.ledger.delivered / agg.funnel.interactions : 0}
        />
      )}
      </>
      )}

      {activeTab === 'distribution' && (
      <>
      {factsQ.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : byItem.length === 0 ? (
        <Card>
          <CardContent className="py-6 text-center text-xs text-[var(--color-fg-subtle)]">{t('insights.chart.empty')}</CardContent>
        </Card>
      ) : (
        <LedgerBars
          rows={byItem}
          totals={itemTotals}
          title={t('insights.distribution.byItem')}
          basis={t('insights.distribution.byItemNote', { n: fmtInt(itemTotals.allocated) })}
        />
      )}
      {/* ── Charts ───────────────────────────────────────────────────── */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Distribution by brand — hide when only one brand is in scope */}
        {byBrand.length > 1 && (
          <ChartCard
            title={t('insights.chart.byBrand')}
            loading={loading}
            empty={byBrand.length === 0}
          >
            <BarChart data={byBrand} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-subtle)" />
              <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--color-fg-muted)' }} tickLine={false} axisLine={false} />
              <YAxis
                type="category"
                dataKey="name"
                width={110}
                tick={{ fontSize: 11, fill: 'var(--color-fg-muted)' }}
                tickLine={false}
              />
              <Tooltip contentStyle={TOOLTIP_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} cursor={TOOLTIP_CURSOR} />
              <Bar dataKey="value" fill={THEME.accent} radius={[0, 6, 6, 0]} />
            </BarChart>
          </ChartCard>
        )}

        <ChartCard
          title={t('insights.chart.topEvents')}
          loading={loading}
          empty={byEvent.length === 0}
        >
          <BarChart data={byEvent} margin={{ bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-subtle)" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11, fill: 'var(--color-fg-muted)' }}
              tickLine={false}
              interval={0}
              angle={-30}
              textAnchor="end"
              height={70}
            />
            <YAxis tick={{ fontSize: 11, fill: 'var(--color-fg-muted)' }} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={TOOLTIP_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} cursor={TOOLTIP_CURSOR} />
            <Bar dataKey="value" fill={THEME.accentHover} radius={[6, 6, 0, 0]} />
          </BarChart>
        </ChartCard>

        <ChartCard
          title={t('insights.chart.topStocks')}
          loading={loading}
          empty={topStocks.length === 0}
        >
          <BarChart data={topStocks} layout="vertical" margin={{ left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-subtle)" />
            <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--color-fg-muted)' }} tickLine={false} axisLine={false} />
            <YAxis
              type="category"
              dataKey="name"
              width={130}
              tick={{ fontSize: 11, fill: 'var(--color-fg-muted)' }}
              tickLine={false}
            />
            <Tooltip contentStyle={TOOLTIP_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} cursor={TOOLTIP_CURSOR} />
            <Bar dataKey="value" fill={THEME.success} radius={[0, 6, 6, 0]} />
          </BarChart>
        </ChartCard>

        <ChartCard
          title={t('insights.chart.byClassification')}
          loading={loading}
          empty={byClassification.length === 0}
        >
          <PieChart>
            <Pie
              data={byClassification}
              dataKey="value"
              nameKey="name"
              outerRadius={90}
              label={(p) => `${p.name}: ${p.value}`}
            >
              {byClassification.map((_, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip contentStyle={TOOLTIP_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} />
            <Legend wrapperStyle={{ fontSize: 11 }} formatter={legendInk} />
          </PieChart>
        </ChartCard>

        <ChartCard
          title={t('insights.chart.byRarity')}
          loading={loading}
          empty={byRarity.length === 0}
        >
          <PieChart>
            <Pie
              data={byRarity}
              dataKey="value"
              nameKey="name"
              outerRadius={90}
              label={(p) => `${p.name}: ${p.value}`}
            >
              {byRarity.map((_, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip contentStyle={TOOLTIP_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} />
            <Legend wrapperStyle={{ fontSize: 11 }} formatter={legendInk} />
          </PieChart>
        </ChartCard>

        <ChartCard
          title={t('insights.chart.usedAvailable')}
          loading={loading}
          empty={stockUsage.length === 0}
        >
          <BarChart data={stockUsage} margin={{ bottom: 50 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-subtle)" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11, fill: 'var(--color-fg-muted)' }}
              tickLine={false}
              interval={0}
              angle={-30}
              textAnchor="end"
              height={60}
            />
            <YAxis tick={{ fontSize: 11, fill: 'var(--color-fg-muted)' }} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={TOOLTIP_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} cursor={TOOLTIP_CURSOR} />
            <Bar dataKey="used" stackId="a" name={t('insights.chart.used')} fill={THEME.accent} />
            <Bar
              dataKey="available"
              stackId="a"
              name={t('insights.chart.available')}
              fill={THEME.success}
              fillOpacity={0.35}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} formatter={legendInk} />
          </BarChart>
        </ChartCard>
      </div>
      </>
      )}

      {activeTab === 'demographics' && (
        /* Demographics aggregated client-side across the SCOPE — the same
           set of events the other tabs read. It used to fan out across
           `eventsForFilter` (brand + project only), so picking an event or
           a date range moved every other tab but this one. */
        <div className="space-y-3">
          <div className="flex items-center justify-end">
            {/* Contextual `?` — opens the Help Center on the entry that
                documents which survey components feed this panel. */}
            <HelpHint section="surveys-demographics" />
          </div>
          {/* Lo que dicen las ENCUESTAS del alcance filtrado, pregunta a
              pregunta; debajo, el resumen por cubos de contactos de siempre. */}
          <SurveyDemographics eventIds={scopedEventIds} />
          <DemographicsPanel
            variant="brand"
            eventIds={scopedEventIds}
          />
        </div>
      )}

      {activeTab === 'compare' && (
        <div className="space-y-4">
          <Card>
            <CardContent className="flex flex-col gap-3 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="dash-mono pr-1 text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: 'var(--color-dash-fg-muted)' }}>{t('insights.compare.by')}</span>
                {(['project', 'tag', 'event', 'custom'] as CompareBy[]).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setCompareBy(mode)}
                    className="rounded-full px-3.5 py-2 text-[13px] font-semibold transition-colors"
                    style={compareBy === mode ? { background: 'var(--color-dash-ink)', color: 'var(--color-dash-lime)' } : { background: 'var(--color-dash-paper-3)', color: 'var(--color-dash-fg-2)' }}
                  >
                    {t(`insights.compare.mode.${mode}`)}
                  </button>
                ))}
                <span className="text-xs" style={{ color: 'var(--color-dash-fg-subtle)' }}>{t(`insights.compare.hint.${compareBy}`, { n: MAX_GROUPS })}</span>
              </div>
              {compareBy !== 'custom' && (
                <div className="max-w-xl">
                  <FilterField label={t('compare.pickGroups', { max: MAX_GROUPS })}>
                    <MultiSelect
                      options={candidateGroups.map((g) => ({ value: g.key, label: g.label, hint: t('compare.eventsN', { n: g.facts.length }) }))}
                      value={pickedGroups[compareBy]}
                      onChange={(next) => setPickedGroups((pg) => ({ ...pg, [compareBy]: next.slice(0, MAX_GROUPS) }))}
                      placeholder={t('compare.pickDefaultGroups', { n: Math.min(MAX_GROUPS, candidateGroups.length) })}
                      searchPlaceholder={t('compare.searchGroups')}
                      summary={(n) => t('compare.groupsPicked', { n })}
                      clearLabel={t('insights.filter.clear')}
                      emptyLabel={t('insights.filter.noMatch')}
                    />
                  </FilterField>
                </div>
              )}
              {compareBy === 'custom' && (
                <div className="grid gap-3 md:grid-cols-3">
                  {customSets.map((ids, i) => (
                    <FilterField key={i} label={t('insights.compare.customSet', { n: String.fromCharCode(65 + i) })}>
                      <MultiSelect
                        options={scopedEvents.map((ev) => ({ value: ev.id, label: ev.name, hint: ev.startDate ? dayjs(ev.startDate).format('MMM D') : undefined }))}
                        value={ids}
                        onChange={(next) => setCustomSets((sets) => sets.map((s, j) => (j === i ? next : s)))}
                        placeholder={t('insights.compare.pickEvents')}
                        searchPlaceholder={t('insights.filter.searchEvents')}
                        summary={(n) => t('insights.filter.eventsPicked', { n })}
                        selectAllLabel={t('insights.filter.selectShown')}
                        clearLabel={t('insights.filter.clear')}
                        emptyLabel={t('insights.filter.noMatch')}
                      />
                    </FilterField>
                  ))}
                  {customSets.length < 3 && (
                    <button type="button" onClick={() => setCustomSets((sets) => [...sets, []])} className="self-end text-sm font-semibold hover:underline" style={{ color: 'var(--color-dash-olive)' }}>
                      {t('insights.compare.addSet')}
                    </button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
          {factsQ.isLoading ? <Skeleton className="h-64 w-full" /> : <CompareMatrix groups={compareGroups} />}
        </div>
      )}

      {activeTab === 'leads' && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              label={t('insights.leads.contacts')}
              value={fmtInt(agg.funnel.contacts)}
              hint={t('insights.recap.contactsHint', { n: scopedEvents.length })}
              valueTone="accent"
            />
            <KpiCard
              label={t('insights.leads.interactions')}
              value={fmtInt(agg.funnel.interactions)}
              hint={
                agg.funnel.contacts
                  ? t('insights.leads.perContact', { n: (agg.funnel.interactions / agg.funnel.contacts).toFixed(1) })
                  : ''
              }
            />
            <KpiCard
              label={t('insights.leads.responses')}
              value={fmtInt(agg.funnel.responses)}
              hint={agg.responseRate.state === 'MEASURED' ? t('insights.leads.responsesHint', { p: fmtPct(agg.responseRate.value) }) : ''}
            />
            <KpiCard
              label={t('insights.leads.consent')}
              value={agg.marketingRate.state === 'MEASURED' ? fmtPct(agg.marketingRate.value) : t('dash.pending')}
              hint={
                agg.marketingRate.state === 'MEASURED'
                  ? t('insights.leads.consentHint', { n: fmtInt(agg.funnel.marketingOptIns), leads: fmtInt(agg.funnel.leads) })
                  : t('insights.feed.consentPending')
              }
              valueTone={agg.marketingRate.state === 'MEASURED' ? 'default' : 'warn'}
            />
          </div>
          {/* Embed the full /contacts surface so managers don't bounce to
              another page — con el alcance de esta pantalla, para que la tabla
              y el CSV cuenten la misma gente que las demás pestañas. Su
              búsqueda y su filtro de consentimiento siguen siendo suyos. */}
          <ContactsPage scope={leadsScope} />
        </>
      )}
    </div>
  );
}

/**
 * PRD §10 — Insights tab bar. Five tabs locked: Recap (Atledi), Throughput,
 * Distribution, Demographics, Leads. Kept inline because it's a thin
 * presentational helper with no reuse elsewhere.
 */
function InsightsTabBar({
  activeTab,
  onChange,
}: {
  activeTab: InsightsTab;
  onChange: (tab: InsightsTab) => void;
}) {
  const t = useT();
  return (
    <div className="inline-flex flex-wrap gap-1 rounded-full border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] p-1 text-[11px] font-bold">
      {INSIGHTS_TABS.map((id) => {
        const active = activeTab === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => onChange(id)}
            className={`rounded-full px-3 py-1 transition-colors ${
              active
                ? 'bg-[var(--color-xk-accent)] text-[var(--color-fg-on-accent)]'
                : 'text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]'
            }`}
          >
            {t(`insights.tabs.${id}`)}
          </button>
        );
      })}
    </div>
  );
}

// `InsightsLeadsTab` stub removed — the Leads tab now embeds the full
// ContactsPage so managers can search and export from one place.

// ── Helpers ───────────────────────────────────────────────────────────────

/**
 * PRD §10 — PDF export. We avoid bundling a PDF library by routing through
 * the browser's native "Save as PDF" print path. The button is hidden for
 * plans that don't include `pdfExport`; gated upgrade text replaces it so
 * the manager understands why.
 */
function PdfExportButton() {
  const t = useT();
  const access = useFeatureAccess('pdfExport');
  if (access.isLoading) return null;
  if (!access.enabled) {
    return (
      <span className="rounded-full bg-[var(--color-accent-15)] px-3 py-1 text-[10px] font-extrabold uppercase tracking-widest text-[var(--color-accent-ink)]">
        {t('insights.pdfPaid')}
      </span>
    );
  }
  return (
    <Button variant="ghost" onClick={() => window.print()}>
      <DownloadIcon className="mr-1 h-3.5 w-3.5" />
      {t('insights.exportPdf')}
    </Button>
  );
}

/** Una lectura corta con su etiqueta: el «feed» del recap. */
/** Las lecturas del recap se escriben al entrar en pantalla, como las de Atledi. */
function FeedCard({ tag, text }: { tag: string; text: string }) {
  const { ref, text: typed, typing } = useTypewriter(text, { step: 6, intervalMs: 24 });
  return (
    <div ref={ref} className="rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-4">
      <span className="mb-1.5 inline-block rounded-full bg-[var(--color-accent-10)] px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest text-[var(--color-accent-ink)]">
        {tag}
      </span>
      <p className="min-h-[2.6em] text-sm leading-relaxed text-[var(--color-fg-muted)]">
        {typed}
        {typing ? <span aria-hidden className="ml-0.5 inline-block w-[2px] bg-[var(--color-accent-ink)] [animation:dash-caret_1s_steps(1)_infinite]">&nbsp;</span> : null}
      </p>
    </div>
  );
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[10px] font-extrabold uppercase tracking-widest text-[var(--color-fg-muted)]">
        {label}
      </span>
      {children}
    </label>
  );
}

function Kpi({
  label,
  value,
  number,
  format,
  hint,
  loading,
}: {
  label: string;
  value: string;
  /** Con `number` y `format`, la cifra cuenta hasta su valor con la rampa. */
  number?: number | null;
  format?: (n: number) => string;
  hint?: string;
  loading?: boolean;
}) {
  const { ref, p } = useScrollRamp(800, number ?? value);
  const shown = number != null && format ? format(Math.round(number * p)) : value;
  return (
    <Card ref={ref}>
      <CardContent className="p-5">
        <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--color-fg-muted)]">
          {label}
        </div>
        {loading ? (
          <Skeleton className="mt-2 h-8 w-24" />
        ) : (
          <div className="mt-1 text-3xl font-extrabold tabular-nums text-[var(--color-xk-lavender)]">
            {shown}
          </div>
        )}
        {hint && (
          <div className="mt-1 text-[11px] text-[var(--color-fg-subtle)]">{hint}</div>
        )}
      </CardContent>
    </Card>
  );
}

function ChartCard({
  title,
  loading,
  empty,
  children,
}: {
  title: string;
  loading: boolean;
  empty: boolean;
  children: React.ReactElement;
}) {
  const t = useT();
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="h-72">
        {loading ? (
          <Skeleton className="h-full w-full" />
        ) : empty ? (
          <div className="flex h-full items-center justify-center text-xs text-[var(--color-fg-subtle)]">
            {t('insights.chart.empty')}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {children}
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Group allocations by a key, summing `used` (= quantity − available).
 * Returns sorted descending and skips zero-sum entries so empty buckets don't
 * pollute the chart.
 */
function bucket(
  rows: XkEventStockStation[],
  key: (a: XkEventStockStation) => number | undefined,
  label: (id: number | undefined) => string | undefined,
): Array<{ name: string; value: number }> {
  const acc: Record<string, { id: number | undefined; value: number }> = {};
  for (const a of rows) {
    const k = key(a);
    if (k == null) continue;
    const used = Math.max(0, (a.quantity ?? 0) - (a.available ?? 0));
    if (used === 0) continue;
    const bucketKey = String(k);
    const cur = acc[bucketKey] ?? { id: k, value: 0 };
    cur.value += used;
    acc[bucketKey] = cur;
  }
  return Object.values(acc)
    .map((v) => ({ name: label(v.id) ?? `#${v.id}`, value: v.value }))
    .sort((a, b) => b.value - a.value);
}

/** Count events currently inside their start/end window. */
function activeWindow(events: XkEvent[]): number {
  const now = Date.now();
  return events.reduce((acc, e) => {
    if (!e.startDate || !e.endDate) return acc;
    const s = new Date(e.startDate).getTime();
    const ee = new Date(e.endDate).getTime();
    return s <= now && now <= ee ? acc + 1 : acc;
  }, 0);
}

function formatMoney(n: number): string {
  if (n === 0) return '$0';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `$${(n / 1_000).toFixed(1)}k`;
  return `$${Math.round(n).toLocaleString()}`;
}

